import React, { useEffect, useMemo, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Loader2, Save, Ban, Pencil, Trash2 } from 'lucide-react';
import Select from 'src/components/ui/BrandSelect';
import { adminPortalSelect } from '../utils/ReactSelectStyles';
import { format, subYears, parseISO } from 'date-fns';
import { Actions as DASHBOARDACTIONS } from '../../store/actions/dashboardActions';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { showLoader } from '../../store/action';
import { useHistory, useParams } from 'react-router-dom';
import { NoPermissions, getReportingYearDates, getMultiYearChartDates } from '../Common/ReusableComponents/Reusable';
import { getChartColors } from '../utils/chartTheme';

export const NetZeroChart = ({ baseYear, multiYearData, targetYear, reduction, targetCoverage, data, useData, branding }) => {
  const { palette, titleColor, dangerColor } = getChartColors(branding);
  const coverageLabel = Array.isArray(targetCoverage)
    ? targetCoverage
        .map((item) => item.replace(/(\d+)/, ' $1').replace(/^./, (c) => c.toUpperCase()))
        .join(', ')
    : '';
  let currentEmissionsData = [];
  // if (!useData) {
  currentEmissionsData = Object.keys(multiYearData || {})
    ?.filter((year) => Number(year) >= Number(baseYear))
    ?.map((year) => {
      const yearData = multiYearData[year];

      const filteredData = Object.keys(yearData)
        ?.filter((key) => targetCoverage?.includes(key))
        ?.reduce((sum, key) => {
          sum += yearData[key];
          return sum;
        }, 0);

      return filteredData;
    }, {});
  /* } else {
    currentEmissionsData = Object.entries(multiYearData)
      .filter(([year]) => Number(year) >= Number(baseYear)) // Keep only years >= baseYear
      .map(([, item]) => item.total);
  } */

  const netZero = useData ? data * (1 - reduction / 100) : currentEmissionsData[0] * (1 - reduction / 100); // Calculate the net zero target

  function calculateDataPoints(currentData, startYear, endYear) {
    // if (useData) {
    const totalYears = endYear - startYear;
    const firstValue = data;
    const decrement = (firstValue - netZero) / totalYears;

    let resultData = [data];

    for (let i = 0; i < totalYears; i++) {
      let value = resultData[resultData.length - 1] - decrement;
      resultData.push(value);
    }

    resultData[resultData.length - 1] = netZero;

    return resultData;
    /* } else {
      const initialLength = currentData?.length;
      const totalYears = endYear - startYear + 1;
      const remainingYears = totalYears - initialLength;

      // Calculate the decrement step to ensure the last value is netZero
      const firstValue = currentData[currentData.length - 1];
      const decrement = (firstValue - netZero) / remainingYears;

      // Initialize the result array with the current data
      console.log('currentData', currentData);
      let resultData = [...currentData];

      // Generate data points from the next year after the initial data to endYear
      for (let i = 0; i < remainingYears; i++) {
        let value = resultData[resultData.length - 1] - decrement;
        resultData.push(value);
      }

      // Ensure the last value is exactly netZero (to account for floating point precision)
      resultData[resultData.length - 1] = netZero;

      return resultData;
    } */
  }

  const netZeroTarget = useData
    ? calculateDataPoints(data, baseYear, targetYear)
    : calculateDataPoints(currentEmissionsData, baseYear, targetYear);

  const options = {
    credits: false,
    chart: {
      type: 'areaspline',
      borderRadius: 15,
      spacing: [20, 20, 20, 20],
    },
    title: {
      text: coverageLabel
        ? `Net Zero Targets by ${targetYear} — ${coverageLabel}`
        : `Net Zero Targets by ${targetYear}`,
      style: {
        fontSize: '18px',
        fontFamily: 'Open Sans',
        fontWeight: '600',
        color: titleColor,
      },
    },
    yAxis: {
      title: {
        text: 'Emissions (tCO2e)',
      },
      labels: {
        style: {
          fontSize: '0.9em',
        },
      },
      plotLines: [
        {
          color: dangerColor,
          dashStyle: 'Dash',
          value: netZero,
          width: 2,
          label: {
            align: 'left',
            style: {
              color: dangerColor,
              fontWeight: 'bold',
              fontSize: '15px',
            },
            text: 'Net Zero',
            x: 15,
          },
          zIndex: 10,
        },
      ],
    },
    xAxis: {
      accessibility: {
        rangeDescription: 'Years',
      },
      categories: Array.from({ length: targetYear - baseYear + 1 }, (_, i) => (baseYear + i).toString()),
    },
    plotOptions: {
      series: {},
    },
    legend: {
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
    },
    tooltip: {
      useHTML: true, // Enable HTML to use custom styling
      formatter: function () {
        // Return the formatted tooltip text
        return (
          `<span style="font-size: 12px;">Year: <b>${this.x}</b></span><br/>` +
          `<span style="font-size: 12px;"><span style="color:${this.color};">\u25CF</span> ${
            this.series.name
          }: <b>${this.y.toFixed(2)}</b></span>`
        );
      },
    },
    series: [
      {
        name: 'Actual Emissions',
        data: [...currentEmissionsData],
        zIndex: 5,
        lineWidth: 1,
        fillOpacity: 1,
        marker: {
          enabled: false,
          symbol: 'circle',
        },
        color: palette?.[0],
        type: 'areaspline',
      },
      {
        name: `Net Zero Target ${targetYear}`,
        data: netZeroTarget,
        marker: {
          enabled: false,
          symbol: 'circle',
        },
        zIndex: 4,
        fillOpacity: 0.2,
        lineWidth: 3,
        color: palette?.[1],
        type: 'areaspline',
      },
    ],
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 600,
          },
          chartOptions: {
            legend: {
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'bottom',
            },
          },
        },
      ],
    },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

const NetZeroModal = ({ open, onOpenChange, deleteTarget, id, history }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent onClose={() => onOpenChange(false)} className='font-body'>
      <DialogHeader>
        <DialogTitle className='font-body'>Delete Net Zero Target</DialogTitle>
      </DialogHeader>
      <p className='text-foreground px-7 py-2'>Are you sure you want to delete this target?</p>
      <DialogFooter className='gap-2 sm:gap-0'>
        <Button
          id='delete-target-button'
          variant='destructive'
          onClick={() => {
            deleteTarget(id);
            onOpenChange(false);
            history.push('/netZero');
          }}>
          Delete
        </Button>
        <Button type='button' variant='secondary' onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const NetZeroTarget = ({
  orgDetails,
  currentOrg,
  branding,
  fields,
  targetCoverage,
  absoluteTargets,
  dashboardData,
  currentYear,
  sumValues,
  saveTargets,
  multiYearData,
  showLoader,
  loader,
  orgTargets,
  getTargets,
  deleteTarget,
  font,
  usageYears,
  netZero,
}) => {
  const { id } = useParams();
  const history = useHistory();
  const [modal, setModal] = useState(false);
  const currentTarget = useMemo(() => {
    return (orgTargets && orgTargets.length > 0 && orgTargets?.find((target) => target.id === id)) || null;
  }, [orgTargets, id]);
  const defaultTarget = {
    targetCoverage: ['scope3'],
    organisation: currentOrg,
    absoluteReduction: NaN,
    targetYearEmissions: NaN,
    scope1: 0,
    scope2: 0,
    scope3: 0,
    baseYearEmissions: 0,
    type: 'Net Zero',
    settingMethod: 'Absolute Contraction',
  };
  const [editedState, setEditedState] = useState(id === 'new' ? defaultTarget : currentTarget);
  const [formulation, showFormulation] = useState(false);
  const [useData, setUseData] = useState(id === 'new' || editedState?.useData || true);
  const [disableInputs, setDisableInputs] = useState(false);
  const [formKey, setFormKey] = useState(Date.now());
  const [edit, setEdit] = useState(id === 'new' || false);
  const [dataType, setDataType] = useState('location');
  const prevOrg = useRef();

  useEffect(() => {
    if (!edit && orgTargets) {
      if (currentTarget) {
        setEditedState(currentTarget);
        setFormKey(Date.now()); // update key to re-render inputs
      } else {
        setEditedState(defaultTarget);
        setFormKey(Date.now()); // update key to re-render inputs
      }
    }
  }, [orgTargets]);

  useEffect(() => {
    //Gets data for charts
    const year = editedState?.['baseYear'];

    if (year) {
      const calculatedDates = getReportingYearDates(year, orgDetails, false);
      const multiYearDates = getMultiYearChartDates(currentYear, orgDetails);
      const isCalendarYearOrg = orgDetails?.reportingYearEndDay == 31 && orgDetails?.reportingYearEndMonth == 12;

      let data = [
        {
          group_by: 'entity',
          field: isCalendarYearOrg
            ? [`${dataType}.calendarYear.scope.3`, `${dataType}.calendarYear.scope.2`, `${dataType}.calendarYear.scope.1`]
            : [
                `${dataType}.reportingYear.scope.3`,
                `${dataType}.reportingYear.scope.2`,
                `${dataType}.reportingYear.scope.1`,
              ],
          beginningStartDate: format(calculatedDates.fromDate, 'yyyy-MM-dd'),
          endStartDate: format(calculatedDates.fromDate, 'yyyy-MM-dd'),
          beginningEndDate: format(calculatedDates.toDate, 'yyyy-MM-dd'),
          endEndDate: format(calculatedDates.toDate, 'yyyy-MM-dd'),
          chart: 'yearly',
        },
        {
          group_by: isCalendarYearOrg ? `${dataType}.calendarYear.year` : `${dataType}.reportingYear.year`,
          field: isCalendarYearOrg
            ? [`${dataType}.calendarYear.scope.3`, `${dataType}.calendarYear.scope.2`, `${dataType}.calendarYear.scope.1`]
            : [
                `${dataType}.reportingYear.scope.3`,
                `${dataType}.reportingYear.scope.2`,
                `${dataType}.reportingYear.scope.1`,
              ],
          beginningStartDate: format(multiYearDates.startDate, 'yyyy-MM-dd'),
          endStartDate: format(multiYearDates.startDate, 'yyyy-MM-dd'),
          beginningEndDate: format(multiYearDates.endDate, 'yyyy-MM-dd'),
          endEndDate: format(multiYearDates.endDate, 'yyyy-MM-dd'),
          chart: 'multiYear',
        },
      ];
      if (currentOrg) {
        if (!orgTargets) {
          getTargets();
        }
        sumValues(data, year, currentOrg, dataType, true);
      }
    }
  }, [dataType]);

  useEffect(() => {
    //Calculate total base year emissions
    const newEditedState = { ...editedState };
    let baseYearEmissions = 0;
    editedState?.['targetCoverage']?.forEach((scope) => {
      if (editedState[scope]) {
        baseYearEmissions += parseFloat(editedState[scope]);
      }
    });
    newEditedState.baseYearEmissions = parseFloat(baseYearEmissions?.toFixed(2));
    setEditedState(newEditedState);
  }, [editedState?.['scope1'], editedState?.['scope2'], editedState?.['scope3'], editedState?.['targetCoverage']]);

  useEffect(() => {
    if (!editedState?.['baseYearEmissions'] && !editedState?.['sectorPathway']) return;

    //Calculate total target year emissions
    const newEditedState = { ...editedState };
    newEditedState.targetYearEmissions =
      parseFloat(editedState?.['baseYearEmissions']) * (1 - absoluteTargets.get(editedState?.['sectorPathway']) / 100);
    setEditedState(newEditedState);
  }, [editedState?.['baseYearEmissions'], editedState?.['sectorPathway']]);

  //Label to show which scopes are in the coverage
  const targetCovLabel = targetCoverage?.find(
    (o) => JSON.stringify(o.value) === JSON.stringify(editedState?.['targetCoverage'])
  )?.label;

  const fieldBorderStyle = { borderWidth: '1px', borderColor: 'var(--input, rgba(45, 74, 62, 0.22))' };

  useEffect(() => {
    //Check if all the fields needed for sbt fomrmulation are set
    if (editedState?.['sectorPathway'] && editedState?.['baseYear'] && editedState?.['targetYear']) {
      showFormulation(true);
    } else {
      showFormulation(false);
    }
  }, [editedState?.['sectorPathway'], editedState?.['baseYear'], editedState?.['targetYear']]);

  useEffect(() => {
    //Copy data from dashboard to the fields if useData is false
    if (!useData && editedState?.['baseYear']) {
      const dashData = dashboardData?.multiYear?.[editedState?.baseYear];
      setEditedState((prevState) => ({
        ...prevState,
        scope1: dashData ? dashData?.['scope1'] : 0,
        scope2: dashData ? dashData?.['scope2'] : 0,
        scope3: dashData ? dashData?.['scope3'] : 0,
      }));
      setFormKey(Date.now()); // update key to re-render inputs
    } else if (useData && id === 'new') {
      // Reset the fields if useData is true
      setEditedState((prevState) => ({
        ...prevState,
        scope1: 0,
        scope2: 0,
        scope3: 0,
        baseYearEmissions: NaN,
        targetYearEmissions: NaN,
      }));
      setFormKey(Date.now()); // update key to re-render inputs
    }
  }, [useData, editedState?.['baseYear'], dashboardData]);

  // helper: returns true if any of the new coverage items already exist on another target
  const hasDuplicateCoverage = (newCoverage = []) => {
    return orgTargets?.some(
      (target) =>
        // if you're editing an existing target, skip comparing it to itself:
        target.id !== editedState?.id &&
        // check for any overlap
        target.targetCoverage?.some((cov) => newCoverage?.includes(cov))
    );
  };

  //Check if all the fields are set to enable save button
  const canSave =
    editedState?.['sectorPathway'] &&
    editedState?.['baseYear'] &&
    editedState?.['targetYear'] &&
    editedState?.['baseYearEmissions'] != 0 &&
    editedState?.['targetYearEmissions'] &&
    !isNaN(editedState?.['targetYearEmissions']) &&
    editedState?.['absoluteReduction'] &&
    !isNaN(editedState?.['absoluteReduction']) &&
    // Check if organisation already has target with that targetCoverage
    !hasDuplicateCoverage(editedState?.['targetCoverage']);

  function formatTargetCoverage(arr) {
    if (!Array.isArray(arr)) return '';
    return arr
      .map(
        (item) =>
          item
            .replace(/(\d+)/, ' $1') // Add space before numbers
            .replace(/^./, (c) => c.toUpperCase()) // Capitalize first letter
      )
      .join(', ');
  }

  function cleanScopes(target) {
    const allScopes = ['scope1', 'scope2', 'scope3'];
    // Get the scopes that should be present
    const presentScopes = Array.isArray(target?.targetCoverage) ? target.targetCoverage : [];
    // Remove scopes not in targetCoverage
    allScopes?.forEach((scope) => {
      if (!presentScopes?.includes(scope)) {
        delete target?.[scope];
      }
    });
    return target;
  }

  function getAnnualReduction(baseYear, targetYear, totalReduction) {
    const years = targetYear - baseYear;
    if (!years || isNaN(totalReduction)) return '';
    const annual = totalReduction / years;
    return annual?.toFixed(2);
  }

  const typeValue = useMemo(() => (useData ? 'manual' : dataType), [useData, dataType]);

  const handleDataType = (event, dataType) => {
    if (!dataType || dataType === typeValue) return;
    if (dataType === 'location' || dataType === 'market') {
      setDataType(dataType);
      setUseData(false);
      setDisableInputs(true);
    } else if (dataType === 'manual') {
      setUseData(true);
      setDisableInputs(false);
    }
  };

  if (!netZero) {
    return <NoPermissions />;
  }

  return (
    <React.Fragment>
      <div className='font-body'>
        <header className='flex flex-wrap items-center justify-between gap-2 border-b border-border pb-4 mb-6'>
          <h2 className='text-lg font-semibold text-foreground uppercase tracking-wide'>
            Absolute Net Zero Targets
          </h2>
          <div className='flex items-center gap-2'>
            {edit ? (
              <>
                <Button
                  onClick={() => {
                    showLoader(true);
                    saveTargets({ ...editedState, useData: useData });
                    history.push('/netZero');
                  }}
                  disabled={loader || !canSave}
                  className='bg-primary text-primary-foreground'>
                  {loader ? (
                    <Loader2 size={16} strokeWidth={1.5} className='animate-spin shrink-0' />
                  ) : (
                    <Save size={16} strokeWidth={1.5} className='shrink-0' />
                  )}
                  <span className='ml-2'>Save</span>
                </Button>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant='ghost'
                        size='icon'
                        className={id === 'new' ? 'hidden' : ''}
                        onClick={() => {
                          const resetTarget = cleanScopes(currentTarget);
                          setEdit(false);
                          setEditedState(resetTarget);
                          setUseData(currentTarget?.useData || true);
                          setFormKey(Date.now());
                        }}>
                        <Ban size={18} strokeWidth={1.5} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Cancel</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            ) : (
              <Button
                id='edit-target-button'
                variant='secondary'
                className='font-semibold'
                onClick={() => setEdit(true)}>
                <Pencil size={16} strokeWidth={1.5} className='shrink-0' />
                <span className='ml-2'>Edit</span>
              </Button>
            )}
            {id !== 'new' && (
              <Button variant='destructive' className='font-semibold' onClick={() => setModal(true)}>
                <Trash2 size={16} strokeWidth={1.5} className='shrink-0' />
                <span className='ml-2'>Delete</span>
              </Button>
            )}
          </div>
        </header>
        <div className='Section 1.1'>
          {fields.map((field, index) => {
            return field?.type === 'input' ? (
              <>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', key: index }}>
                  <div className='SBT-div'>
                    <div className='SBT-form'>
                      <span className='SBT-form-label'>
                        {field?.name === 'baseYearEmissions'
                          ? field?.label.replace('*', targetCovLabel || '')
                          : field?.label}
                      </span>
                      <div className='SBT-input-div'>
                        {edit ? (
                          <input
                            key={`index-${formKey}`}
                            className='h-9 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm'
                            type={field?.format}
                            step={'any'}
                            value={field?.value || editedState?.[field?.name] || ''}
                            disabled={
                              field?.disabled || disableInputs || !editedState?.targetCoverage?.includes(field?.name)
                            }
                            style={{
                              textAlign: 'center',
                              height: '32px',
                              width: '80%',
                              cursor: 'text',
                              ...fieldBorderStyle,
                            }}
                            onChange={(e) => {
                              setEditedState((prevState) => ({
                                ...prevState,
                                [field?.name]: e.target.value,
                              }));
                            }}></input>
                        ) : (
                          <input
                            key={`index-${formKey}`}
                            className='h-9 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm'
                            type={field?.format}
                            step={'any'}
                            value={editedState?.[field?.name]}
                            disabled={true}
                            style={{
                              textAlign: 'center',
                              height: '32px',
                              width: '80%',
                              cursor: 'text',
                              ...fieldBorderStyle,
                            }}></input>
                        )}
                      </div>{' '}
                    </div>
                    <div className='SBT-adornment'>
                      {field?.adornment}
                      <div
                        style={{
                          display: field?.name === 'scope1' && loader && edit ? 'flex' : 'none',
                          justifyContent: 'center',
                          marginLeft: 10,
                          flexDirection: 'row',
                          alignItems: 'center',
                          fontSize: '14px',
                          color: '#555',
                        }}>
                        <Loader2 size={14} strokeWidth={1.5} className='animate-spin' />
                        <span style={{ marginLeft: '5px' }}>Loading Organisation Data...</span>
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      display:
                        field?.copy === true && editedState?.['targetCoverage']?.includes(field?.name) ? 'flex' : 'none',
                      marginTop: 10,
                    }}></div>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <div className='SBT-div'>
                    <div className='SBT-form'>
                      <span className='SBT-form-label'>{field?.label}</span>
                      {edit ? (
                        <div className='SBT-select-div'>
                          <div style={{ marginLeft: '10%' }}>
                            <Select
                              key={`index-${formKey}`}
                              isClearable
                              menuPlacement={'auto'}
                              styles={{
                                ...adminPortalSelect,
                                control: (provided) => ({
                                  ...provided,
                                  width: '89%',
                                  ...fieldBorderStyle,
                                }),
                                menu: (provided) => ({
                                  ...provided,
                                  width: '89%',
                                }),
                                container: (provided) => ({
                                  ...provided,
                                  marginLeft: 0,
                                }),
                              }}
                              options={field?.options}
                              onChange={(e) => {
                                if (e === null) {
                                  if (field?.name === 'sectorPathway') {
                                    setEditedState((prevState) => ({
                                      ...prevState,
                                      [field?.name]: null,
                                      absoluteReduction: NaN,
                                    }));
                                  }
                                  setEditedState((prevState) => ({
                                    ...prevState,
                                    [field?.name]: null,
                                  }));
                                } else if (field?.name === 'sectorPathway') {
                                  setEditedState((prevState) => ({
                                    ...prevState,
                                    [field?.name]: e.value,
                                    absoluteReduction: absoluteTargets.get(e.value),
                                  }));
                                } else {
                                  setEditedState((prevState) => ({
                                    ...prevState,
                                    [field?.name]: e.value,
                                  }));
                                }
                              }}
                              value={
                                field?.name === 'targetCoverage'
                                  ? field?.options?.find(
                                      (o) => JSON.stringify(o.value) === JSON.stringify(editedState?.['targetCoverage'])
                                    )
                                  : field?.options?.find((o) => o.value === editedState?.[field?.name])
                              }
                            />
                          </div>
                        </div>
                      ) : (
                        <div className='SBT-input-div'>
                          <input
                            key={`index-${formKey}`}
                            className='h-9 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm'
                            type={'text'}
                            step={'any'}
                            value={
                              field?.name === 'targetCoverage'
                                ? formatTargetCoverage(editedState?.[field?.name])
                                : editedState?.[field?.name]
                            }
                            disabled={true}
                            style={{
                              textAlign: 'center',
                              height: '32px',
                              width: '80%',
                              cursor: 'text',
                              ...fieldBorderStyle,
                            }}></input>
                        </div>
                      )}
                    </div>
                    <div className='SBT-adornment'>
                      {field.name === 'targetCoverage'
                        ? hasDuplicateCoverage(editedState?.['targetCoverage']) && field?.adornment
                        : field?.adornment}
                      <div
                        style={{
                          display: field?.name === 'baseYear' && editedState?.['baseYear'] && edit ? 'flex' : 'none',
                        }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '12px',
                            border: '1px solid #ccc',
                            position: 'relative',
                            borderRadius: '8px',
                            padding: '16px 12px 12px',
                            flexWrap: 'wrap',
                            marginTop: '6px',
                          }}>
                          <span
                            style={{
                              position: 'absolute',
                              top: '-10px', // pulls it up over the border
                              left: '50%', // horizontally center
                              transform: 'translateX(-50%)',
                              background: 'white', // same as page bg to mask the border
                              padding: '0 8px',
                              fontSize: '13px',
                              fontWeight: 'bold',
                              zIndex: 2,
                              whiteSpace: 'nowrap',
                            }}>
                            Use Organisation Data
                          </span>
                          <div className='flex gap-2'>
                            <Button
                              variant={typeValue === 'location' ? 'default' : 'secondary'}
                              size='sm'
                              className='text-[13px] font-semibold'
                              onClick={(e) => handleDataType(e, 'location')}>
                              Location-Based Emissions
                            </Button>
                            <Button
                              variant={typeValue === 'market' ? 'default' : 'secondary'}
                              size='sm'
                              className='text-[13px] font-semibold'
                              onClick={(e) => handleDataType(e, 'market')}>
                              Market-Based Emissions
                            </Button>
                            <Button
                              variant={typeValue === 'manual' ? 'default' : 'secondary'}
                              size='sm'
                              className='text-[13px] font-semibold'
                              onClick={(e) => handleDataType(e, 'manual')}>
                              Enter Manually
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            );
          })}
        </div>
        <div className='Section 1.2' style={{ width: '100%' }}>
          <h5 style={{ marginLeft: 10, marginTop: 20 }}>
            <b>Absolute Target Results</b>
          </h5>
          <hr className='border-border mb-2.5' />
          <table style={{ width: '100%' }}>
            <thead>
              <tr>
                <th></th>
                <th style={{ textAlign: 'center' }}>Base Year ({editedState?.['baseYear']})</th>
                <th style={{ textAlign: 'center' }}>Target Year ({editedState?.['targetYear']})</th>
                <th style={{ textAlign: 'center' }}>% Absolute Reduction</th>
                <th style={{ textAlign: 'center' }}>% Annual Reduction</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  style={{
                    width: '16.6%',
                    padding: '5px',
                    borderRadius: '3px',
                    paddingLeft: '20px',
                    paddingBottom: '15px',
                  }}>
                  {(targetCovLabel ? targetCovLabel : '') + ' Emissions (tCO2e)'}
                </td>
                <td style={{ width: '16.6%', paddingBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <input
                      className='h-9 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm'
                      type={'number'}
                      value={
                        typeof editedState?.baseYearEmissions === 'number' ? editedState?.baseYearEmissions?.toFixed(2) : ''
                      }
                      disabled={true}
                      style={{
                        textAlign: 'center',
                        height: '32px',
                        width: '80%',
                        cursor: 'text',
                      }}></input>
                  </div>
                </td>
                <td style={{ width: '16.6%', paddingBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <input
                      className='h-9 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm'
                      key={`target-${formKey}`}
                      type={'number'}
                      value={
                        Number.isNaN(editedState?.['targetYearEmissions'])
                          ? ''
                          : editedState?.['targetYearEmissions']?.toFixed(2)
                      }
                      disabled={true}
                      style={{
                        textAlign: 'center',
                        height: '32px',
                        width: '80%',
                        cursor: 'text',
                      }}></input>
                  </div>
                </td>
                <td style={{ width: '16.6%', paddingBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <input
                      className='h-9 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm'
                      key={`reduction-${formKey}`}
                      type={'number'}
                      value={isNaN(editedState?.['absoluteReduction']) ? '' : editedState?.['absoluteReduction']?.toFixed(2)}
                      disabled={true}
                      style={{
                        textAlign: 'center',
                        height: '32px',
                        width: '80%',
                        cursor: 'text',
                      }}></input>
                  </div>
                </td>
                <td style={{ width: '16.6%', paddingBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <input
                      className='h-9 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm'
                      key={`annualReduction-${formKey}`}
                      type={'number'}
                      value={
                        isNaN(editedState?.['absoluteReduction']) ||
                        !editedState?.['baseYear'] ||
                        !editedState?.['targetYear']
                          ? ''
                          : getAnnualReduction(
                              editedState?.['baseYear'],
                              editedState?.['targetYear'],
                              editedState?.['absoluteReduction']
                            )
                      }
                      disabled={true}
                      style={{
                        textAlign: 'center',
                        height: '32px',
                        width: '80%',
                        cursor: 'text',
                      }}></input>
                  </div>
                </td>
              </tr>
              <tr>
                <td style={{ paddingTop: 10, paddingLeft: 20, width: '17%' }}>Net Zero Target Wording</td>
                <td colSpan={4}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <input
                      className='h-9 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm'
                      type={'text'}
                      value={
                        formulation
                          ? `${orgDetails?.name} commits to reduce ${targetCovLabel} emissions ${Math.round(
                              editedState?.['absoluteReduction']
                            )}% by ${editedState?.['targetYear']} from a ${editedState?.['baseYear']} base year`
                          : ''
                      }
                      disabled={true}
                      style={{
                        textAlign: 'center',
                        height: '32px',
                        width: '95%',
                        cursor: 'text',
                      }}></input>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 20 }}>
          {formulation && editedState?.['baseYearEmissions'] ? (
            <NetZeroChart
              targetYear={editedState?.['targetYear']}
              baseYear={editedState?.['baseYear']}
              reduction={editedState?.['absoluteReduction']}
              targetCoverage={editedState?.['targetCoverage']}
              data={editedState?.['baseYearEmissions']}
              useData={useData}
              multiYearData={multiYearData}
              branding={branding}
            />
          ) : null}
        </div>
      </div>
      <NetZeroModal
        open={modal}
        onOpenChange={setModal}
        deleteTarget={deleteTarget}
        id={id}
        history={history}
      />
    </React.Fragment>
  );
};

const mapStateToProps = (state) => {
  const currentYear = new Date().getFullYear();
  const baseYears = Array.from({ length: currentYear - 2019 + 1 }, (_, i) => 2019 + i);
  const targetYears = Array.from({ length: 11 }, (_, i) => 2040 + i);

  const sectorPathways = [
    { label: 'Cross-sector pathway', value: 'Cross-sector pathway' },
    // { label: 'FLAG (Agriculture only)', value: 'FLAG', isDisabled: true },
    { label: 'Iron and steel', value: 'Iron and steel' },
    { label: 'Cement', value: 'Cement' },
    // { label: 'Services - Buildings', value: 'Services - Buildings' },
    // { label: 'Residential - Buildings', value: 'Residential - Buildings' },
    // { label: 'Power generation', value: 'Power generation', isDisabled: true },
  ];

  const targetCoverage = [
    { label: 'Scope 1', value: ['scope1'] },
    { label: 'Scope 2', value: ['scope2'] },
    { label: 'Scope 1 & 2', value: ['scope1', 'scope2'] },
    { label: 'Scope 3', value: ['scope3'] },
    { label: 'Scopes 1, 2 & 3', value: ['scope1', 'scope2', 'scope3'] },
  ];

  const absoluteTargets = new Map();
  absoluteTargets.set('Cross-sector pathway', 90);
  absoluteTargets.set('FLAG (Agriculture only)', 72);
  absoluteTargets.set('Iron and steel', 93);
  absoluteTargets.set('Cement', 95);
  absoluteTargets.set('Services - Buildings', 99.6);
  absoluteTargets.set('Residential - Buildings', 97.89);

  const fields = [
    {
      type: 'select',
      name: 'targetCoverage',
      label: 'Target Coverage',
      required: true,
      options: targetCoverage,
      adornment: (
        <span style={{ color: 'red' }}>
          There is already an existing target containing this coverage, to save this, delete the previous target.
        </span>
      ),
    },
    {
      type: 'input',
      format: 'text',
      name: 'settingMethod',
      label: 'Target Setting Method',
      value: 'Absolute Contraction',
      disabled: true,
      adornment:
        'For intensity targets in the Cement, Iron & Steel or Power sectors, enter data in the Intensity Targets section.',
    },
    {
      type: 'select',
      name: 'baseYear',
      label: 'Base Year',
      required: true,
      options: baseYears?.map((y) => {
        return { label: y.toString(), value: y };
      }),
    },
    {
      type: 'select',
      name: 'targetYear',
      label: 'Target Year',
      required: true,
      options: targetYears?.map((y) => {
        return { label: y.toString(), value: y };
      }),
    },
    {
      type: 'select',
      name: 'sectorPathway',
      label: 'Sector Pathway',
      required: true,
      options: sectorPathways,
      adornment: 'For all sectors except Cement and Iron & Steel, select Cross-sector pathway',
    },
    {
      name: 'scope1',
      type: 'input',
      format: 'number',
      label: 'Scope 1 Emissions',
      required: true,
      adornment: 'tCO2e',
      copy: true,
    },
    {
      name: 'scope2',
      type: 'input',
      format: 'number',
      label: 'Scope 2 Emissions',
      required: true,
      adornment: 'tCO2e',
      copy: true,
    },
    {
      name: 'scope3',
      type: 'input',
      format: 'number',
      label: 'Scope 3 Emissions',
      required: true,
      adornment: 'tCO2e',
      copy: true,
    },
    {
      name: 'baseYearEmissions',
      type: 'input',
      format: 'number',
      label: 'Total Emissions in * (tCO2e)',
      disabled: true,
      adornment: 'tCO2e',
    },
  ];

  const features = state?.features;
  const availableFeatures = state.organisation?.details?.features;
  const sbtFeature = features?.find((feature) => feature.featureKey === 'SCIENCE_BASED_TARGETS');
  const netZero = availableFeatures?.includes('SCIENCE_BASED_TARGETS') || sbtFeature?.enabledByDefault || false;
  return {
    orgDetails: state?.organisation?.details,
    currentOrg: state.currentOrganisation,
    SBTExcel: state?.whiteLabelAdmin?.scienceBasedTarget,
    branding: state.branding,
    fields,
    targetCoverage,
    absoluteTargets,
    dashboardData: state?.dashboard?.data,
    currentYear,
    multiYearData: state.dashboard?.data?.multiYear,
    loader: state?.showLoader,
    orgTargets: state?.dashboard?.targets,
    font: state?.branding?.fonts?.[0]?.font?.body || {},
    usageYears: state?.organisation?.details?.usageYears,
    netZero,
  };
};

const mapDispatchToProps = (dispatch) => ({
  sumValues: (data, year, org, dataType, ignoreCache) =>
    dispatch(DASHBOARDACTIONS.sumValues(data, year, org, dataType, ignoreCache)),
  saveTargets: (targets) => dispatch(DASHBOARDACTIONS.saveTargets(targets)),
  showLoader: (show) => dispatch(showLoader(show)),
  getTargets: () => dispatch(DASHBOARDACTIONS.getTargets()),
  deleteTarget: (targetId) => dispatch(DASHBOARDACTIONS.deleteTarget(targetId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(NetZeroTarget);
